import {
    Button,
    Container,
    DateRangePicker,
    DateRangePickerProps,
    ExpandableSection,
    Header,
    Icon,
    Select,
    SpaceBetween,
    Tabs,
    TextContent
} from '@amzn/awsui-components-react';
import {
    EuiCallOut,
    EuiEmptyPrompt,
    EuiLink
} from '@elastic/eui';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ExternalDashboard } from '../../../Components/Cloudwatch/ExternalDashboard';
import { ExternalWidget } from '../../../Components/Cloudwatch/model';
import { useToastNotifications } from '../../../../hooks/useToastList';
import { AppContext } from '../../../../utils/appContext';
import { createHandleDateChange } from '../../../../utils/dashboardUtils';
import {
    DAYS_15,
    DAYS_455,
    DAYS_63,
    DAY_1,
    HOURS_3,
    HOUR_1,
    METRIC_RETENTION_INFO,
    MINUTE_1,
    MINUTE_15,
    MINUTE_5,
    SEC_1,
} from '../../Account/Dashboards/constants';
import {
  getFlexoMetrics,
  i18nStrings,
  periodOptions as periodDropdownOptions,
  relativeOptions,
  timezoneOptions,
} from "../../Account/Dashboards/utils";
import { set } from 'ace-builds-internal/config';
import { isValidRange } from '../../../../utils/timeUtils';

const LoadGraphs = (props: any) => (
    <EuiEmptyPrompt
        title={<h4>Load cloud watch graphs ?</h4>}
        actions={[
            <Button variant="normal" onClick={props.onClick}>
                Display graphs
            </Button>,
        ]}
    />
);

interface DashboardProps {
    placementConstraintId: string;
    awsAccountId: string;
    shared?: {
        endTime: number; // Epoch time
        startTime: number; // Epoch time
        title: string;
        metricName?: string;
        stats?: string;
        splitByResource?: string;
        dim?: string;
    };
}

const JunoPCDashboards = (props: DashboardProps) => {
    const appContext = useContext(AppContext);
    const toastNotifications = useToastNotifications();
    const cloudWatchProps = {
        region: appContext.region.publicRegion,
        rootAccountId: appContext.rootAccountId,
        isOptInRegion: appContext.region.isOptInRegion || false,
    };

    const [loadGraphs, setLoadGraphs] = useState(false);
    const [, setShowFullScreen] = useState<boolean>(false);

    const [date, setDate] = useState<any>({
        amount: 1,
        unit: 'week',
        type: 'relative',
    });

    const [periodOptions, setPeriodOptions] = useState(periodDropdownOptions);

    const [selectedPeriod, setSelectedPeriod] = useState({
        label: '1h',
        value: '3600',
        text: 3600,
    });

    // Valid parsed date from moment
    const [metricRange, setMetricRange] = useState<any>({
        startTime: props.shared
            ? moment(props.shared?.startTime).unix()
            : moment().subtract(1, 'weeks').unix(),
        endTime: props.shared
            ? moment(props.shared?.endTime).unix()
            : moment().unix(),
        period: selectedPeriod.text,
    });

    // Defaults to browser time and not to UTC
    const [utcOffset, setUtcOffset] = useState<number>(0);

    // const [timezoneValue, setTimezoneValue] = useState('Browser Timezone');
    const [selectedOption, setSelectedOption] = React.useState({
        inputDisplay: 'Browser Timezone',
        label: 'Browser Timezone',
        value: 'Browser Timezone',
        text: 0,
    });

    const handleDateChange = useCallback(
        createHandleDateChange(
            periodOptions,
            selectedPeriod,
            setPeriodOptions,
            setSelectedPeriod,
            setDate,
            setMetricRange,
            toastNotifications
        ),
        [selectedPeriod, periodOptions, toastNotifications]
    );

    const handleTimezoneChange = (selectedOption) => {
        setUtcOffset(selectedOption.text);
        setSelectedOption(selectedOption);
    };

    const handlePeriodChange = (selectedPeriodOption) => {
        setSelectedPeriod(selectedPeriodOption);
    };

    useEffect(() => {
        var startTime = metricRange.startTime;
        var endTime = metricRange.endTime;
        var period = selectedPeriod.text;
        var newMetric = { startTime: startTime, endTime: endTime, period: period };
        setMetricRange(newMetric);
    }, [selectedPeriod, metricRange.startTime, metricRange.endTime]);

    const getAllMetrics = () => [
        ...(getFlexoMetrics(
            "indexing",
            props.placementConstraintId,
            props.awsAccountId,
            appContext.region.publicRegion,
            false
        ) as ExternalWidget[]),
        ...(getFlexoMetrics(
            "search",
            props.placementConstraintId,
            props.awsAccountId,
            appContext.region.publicRegion,
            false
        ) as ExternalWidget[]),
    ];

    const tabs = [
        {
            id: 'indexing',
            name: 'Indexing Metrics',
            label: 'Indexing Metrics',
            content: loadGraphs ? (
                <div>
                    <ExternalDashboard
                        widgets={[
                            ...(getFlexoMetrics(
                                "indexing",
                                props.placementConstraintId,
                                props.awsAccountId,
                                appContext.region.publicRegion,
                                false
                            ) as ExternalWidget[]),
                        ]}
                        metricRange={metricRange}
                        utcOffset={utcOffset}
                    />
                </div>
            ) : (
                <LoadGraphs onClick={() => setLoadGraphs(true)} />
            ),
            disabled: false,
        },
        {
            id: 'search',
            name: "Search Metrics",
            label: "Search Metrics",
            content: loadGraphs ? (
                <div>
                    <ExternalDashboard
                        widgets={[
                            ...(getFlexoMetrics(
                                "search",
                                props.placementConstraintId,
                                props.awsAccountId,
                                appContext.region.publicRegion,
                                false
                            ) as ExternalWidget[]),
                        ]}
                        metricRange={metricRange}
                        utcOffset={utcOffset}
                    />
                </div>
             ) : (
                 <LoadGraphs onClick={() => setLoadGraphs(true)} />
             ),
            disabled: false,
        },
    ];
    const renderSharedMetrics = () => {
        const selectedMetric = getAllMetrics().find(
            (metric) => metric.title === props.shared?.title.replace(/_/g, ' ')
        );
        if (selectedMetric) {
            return (
                <ExternalDashboard
                    widgets={[selectedMetric]}
                    metricRange={metricRange}
                    utcOffset={utcOffset}
                />
            );
        } else {
            return (
                <EuiCallOut
                    title={
                        <span>
                          Metric <b>{props.shared?.title}</b> is not valid, ensure this
                          metric is supported by Tumbler
                        </span>
                    }
                    color="warning"
                    iconType="alert"
                    size="m"
                >
                    <p>
                        {' '}
                        Navigate to Account overview{' '}
                        <EuiLink href={`#/${props.placementConstraintId}`}>
                            {props.placementConstraintId}
                        </EuiLink>{' '}
                        for more details.
                    </p>
                </EuiCallOut>
            );
        }
    };
    return (
        <Container
            disableContentPaddings
            header={
                <Header
                    variant="h2"
                    actions={
                        <SpaceBetween direction="horizontal" size="s">
                            <DateRangePicker
                                value={date}
                                i18nStrings={i18nStrings}
                                placeholder="Filter by a date and time range"
                                onChange={handleDateChange}
                                relativeOptions={relativeOptions}
                                isValidRange={isValidRange}
                                showClearButton={false}
                            />
                            <TextContent>
                                <strong>Period :</strong>
                            </TextContent>
                            <Select
                                selectedOption={selectedPeriod}
                                onChange={({ detail }) =>
                                    handlePeriodChange(detail.selectedOption)
                                }
                                options={periodOptions}
                                selectedAriaLabel="Selected"
                            />
                            <TextContent>
                                <strong>Timezone :</strong>
                            </TextContent>
                            <Select
                                selectedOption={selectedOption}
                                onChange={({ detail }) =>
                                    handleTimezoneChange(detail.selectedOption)
                                }
                                options={timezoneOptions}
                                selectedAriaLabel="Selected"
                            />
                            <Button
                                variant="link"
                                onClick={() =>
                                    setShowFullScreen(
                                        (latestFullScreen: boolean) => !latestFullScreen
                                    )
                                }
                            >
                                <Icon name="view-full" size="normal" variant="normal" />
                            </Button>
                        </SpaceBetween>
                    }
                >
                    JunoPCDashboards
                </Header>
            }
        >
            <ExpandableSection
                header="Retention period of all metrics"
                className="expandableSection"
            >
                {METRIC_RETENTION_INFO.map((row) => (
                    <>
                        {row}
                        <br />
                    </>
                ))}
            </ExpandableSection>
            {props.shared ? renderSharedMetrics() : <Tabs tabs={tabs} />}
        </Container>
    );
};

export { JunoPCDashboards };
